import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/library/avatar";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/library/popover";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/library/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { AlertTriangle, MoreHorizontal, Plus, Star, UserRoundPlus, X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  useActiveLeadFilterParams,
  parseCfPairsFromParams,
} from "@/features/leads/api/useActiveLeadFilterParams";
import { getInitials } from "@/utils/convetors";
import {
  setIsAssign,
  setIsPeerTransfer,
} from "@/features/leads/stores/leadFunctionalSlice";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { selectCurrentUserId } from "@/features/auth/currentUser";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import {
  roleHolderLevels,
  useAssignationPermissions,
  type RoleHolderLevels,
} from "@/features/leads/hooks/useAssignationPermissions";
import useRolesPipeline from "@/features/users/api/useRolesPipeline";
import {
  assignmentRequestErrorMessage,
  usePendingAssignmentRequests,
  useResolveAssignmentRequest,
  type PendingAssignmentRequest,
} from "@/features/leads/api/useAssignmentRequests";
import AssignRequestDialog from "@/features/leads/components/assign-request/AssignRequestDialog";
import { alertTimeAgo } from "@/features/alerts/types";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { usePermissions } from "@/rbac/usePermissions";
import { useCanEditDeal } from "@/rbac/useCanEditDeal";
import { useDealsDisabled, useShowPreviousAssignees } from "@/features/leads/api/useLeadFormConfig";
import {
  useLeadRevokeSummary,
  type LeadRevokeCause,
} from "@/features/leads/api/useLeadRevokeSummary";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import useVariants from "@/features/videoProduction/api/useVariants";
import { getVideoModuleEntry } from "@/module/videoProduction/helpers/useVideoChildModuleData";
import useProgramVariants from "@/features/academy/api/useProgramVariants";
import { getAcademyModuleEntry } from "@/module/academy/helpers/useAcademyChildModuleData";

const SUB_STATUS_COLORS: Record<string, string> = {
  "In Progress": "#ff8d13",
  Contacted: "#335cff",
  "Documents Submitted": "#ac1cff",
  "Follow-up Scheduled": "#3bd8ff",
  "Not Responded": "#697571",
  Admitted: "#FF8D13",
};

const AVATAR_COLORS = [
  { bg: "#e1e4ea", text: "#0e121b" },
  { bg: "#c0d5ff", text: "#122368" },
  { bg: "#ffecc0", text: "#624c18" },
  { bg: "#c1e1d5", text: "#051912" },
  { bg: "#ffd4d4", text: "#661313" },
] as const;

/** Human labels for why a former assignee was revoked (admin "Previous" list). */
const REVOKE_CAUSE_LABEL: Record<LeadRevokeCause, string> = {
  cf_access_lost: "Lost field access",
  replaced_same_role: "Replaced by same role",
  manual: "Removed",
};

/** A former (revoked) assignee, shown dimmed to admins when the tenant opts in. */
type PreviousUser = {
  key: string;
  userId: number;
  name: string;
  roleName: string;
  level: "lead" | "child";
  childName: string | null;
  revokedAt: string | null;
  revokedByName: string | null;
  cause: LeadRevokeCause | null;
};

const fmtCurrency = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

function DealChip({
  child,
  isSelected,
  onClick,
  variant,
  canRename,
  onRename,
}: {
  child: any;
  isSelected: boolean;
  onClick: () => void;
  variant?: any;
  canRename?: boolean;
  onRename?: (name: string) => void;
}) {
  const isActive = !child.isFroze;
  const [hoverOpen, setHoverOpen] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Double-click → inline rename. The chip is replaced by an autofocused
  // input; Enter / blur commits, Escape cancels. Gated by `canRename`
  // (lead-edit) — the server re-checks the per-stage rule on save.
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setHoverOpen(true), 250);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setHoverOpen(false), 120);
  };

  const beginEdit = () => {
    if (!canRename || !onRename) return;
    clearTimeout(hoverTimeout.current);
    setHoverOpen(false);
    setDraftName(child?.name ?? "");
    setIsEditing(true);
  };

  const commitEdit = () => {
    const next = draftName.trim();
    setIsEditing(false);
    if (next && next !== (child?.name ?? "").trim()) {
      onRename?.(next);
    }
  };

  const cancelEdit = () => setIsEditing(false);

  useEffect(() => {
    if (!isEditing) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draftName}
        onChange={(e) => setDraftName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitEdit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancelEdit();
          }
        }}
        onBlur={commitEdit}
        onClick={(e) => e.stopPropagation()}
        maxLength={120}
        className="h-[28px] w-[150px] rounded-[7px] border border-[#46a580] bg-white px-2.5 text-[11.5px] font-medium text-[#1e3a2c] outline-none ring-2 ring-[#46a580]/25"
      />
    );
  }

  const dealValue =
    child.totalDealValue ?? child.netAmount ?? variant?.price ?? 0;
  const discount = child.discountAmount ?? 0;
  const net = child.netAmount ?? dealValue;
  const received = child.totalPaid ?? 0;
  const pending = child.pendingAmount ?? 0;
  const hasFinancials = dealValue > 0 || net > 0;

  return (
    <Popover open={hoverOpen} onOpenChange={setHoverOpen}>
      <PopoverTrigger asChild>
        <button
          data-selected={isSelected}
          onClick={onClick}
          onDoubleClick={beginEdit}
          title={canRename ? "Double-click to rename" : undefined}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={
            isSelected
              ? {
                  background:
                    "linear-gradient(135deg, #3a8f6a 0%, #1a3a2a 100%)",
                }
              : undefined
          }
          className={`
            flex items-center gap-1.5 whitespace-nowrap h-[28px] rounded-[7px] px-2.5 text-[11.5px]
            border transition-all duration-150 cursor-pointer select-none
            ${
              isSelected
                ? "text-white border-transparent shadow-[0_1px_4px_rgba(26,58,42,0.25)]"
                : isActive
                  ? "bg-white text-[#1e3a2c] border-[#c1e1d5] hover:border-[#46a580] hover:bg-[#f5fbf8] shadow-[0_0.5px_1px_rgba(0,0,0,0.04)]"
                  : "bg-[#fafafa] text-[#9ba3a0] border-[#e8eae9] hover:bg-[#f4f4f4] hover:border-[#d5d7d6]"
            }
          `}
        >
          <span
            className={`w-[5px] h-[5px] rounded-full shrink-0 ${
              isSelected
                ? "bg-white/80"
                : isActive
                  ? "bg-[#46a580]"
                  : "bg-[#c5c9c7]"
            }`}
          />
          <span className="font-medium truncate max-w-[110px]">
            {child?.name}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        className="w-[220px] p-0 rounded-[10px] border border-[#e2e4e3] shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-3 py-2.5 flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12px] font-semibold text-[#051912] truncate leading-tight">
              {child?.name}
            </p>
            <span
              className={`w-[5px] h-[5px] rounded-full shrink-0 ${
                isActive ? "bg-[#46a580]" : "bg-[#d4a344]"
              }`}
            />
          </div>

          <div className="flex items-center gap-1.5 text-[10.5px] text-[#7a8380] flex-wrap">
            <span className="truncate">{child?.stage?.name || "—"}</span>
            <span className="text-[#d6d8d7]">/</span>
            <span className="truncate">{child?.status?.name || "—"}</span>
            {child?.subStatus?.name && (
              <>
                <span className="text-[#d6d8d7]">/</span>
                <span className="truncate">{child.subStatus.name}</span>
              </>
            )}
          </div>

          {hasFinancials && (
            <div className="mt-0.5 pt-1.5 border-t border-[#f0f2f1]">
              <div className="grid grid-cols-3 gap-1">
                <div className="flex flex-col items-center">
                  <span className="text-[8px] font-semibold text-[#9ba3a0] uppercase tracking-[0.3px]">
                    Value
                  </span>
                  <span className="text-[10px] font-semibold text-[#051912] tabular-nums">
                    ₹{fmtCurrency(dealValue)}
                  </span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-[8px] font-semibold text-[#9ba3a0] uppercase tracking-[0.3px]">
                    Disc.
                  </span>
                  <span className="text-[10px] font-semibold text-[#EF5350] tabular-nums">
                    {discount > 0 ? `-₹${fmtCurrency(discount)}` : "—"}
                  </span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-[8px] font-semibold text-[#9ba3a0] uppercase tracking-[0.3px]">
                    Net
                  </span>
                  <span className="text-[10px] font-bold text-[#267355] tabular-nums">
                    ₹{fmtCurrency(net)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 mt-1 pt-1 border-t border-[#f0f2f1]">
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-semibold text-[#9ba3a0] uppercase">
                    Received
                  </span>
                  <span className="text-[10px] font-bold text-[#2d7a5a] tabular-nums">
                    ₹{fmtCurrency(received)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-semibold text-[#9ba3a0] uppercase">
                    Pending
                  </span>
                  <span className="text-[10px] font-bold text-[#c4630a] tabular-nums">
                    ₹{fmtCurrency(pending)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function StatusAndUsersSection() {
  const dispatch = useAppDispatch();
  const { lead, activeData } = useActiveDataMutation();
  const { handleChildChange, allChildren, routeStageId } = useChildForm();
  const { pipeline } = useLeadPipeline();
  const { industry } = useCurrentIndustry();
  const { variants } = useVariants();
  const { variants: academyVariants } = useProgramVariants();
  const isVideoProduction = industry?.id === 4;
  const isAcademy = industry?.id === 5;
  const [isHovered, setIsHovered] = useState(false);
  const dealsScrollRef = useRef<HTMLDivElement>(null);
  const initialScrollDone = useRef(false);

  const variantLookup = useMemo(() => {
    if (!allChildren?.length) return new Map();
    const map = new Map<number, any>();

    if (isVideoProduction && variants?.length) {
      for (const c of allChildren) {
        const entry = getVideoModuleEntry(c);
        const vId = entry?.id;
        if (vId != null) {
          const v = variants.find((x: any) => x.id === vId);
          if (v) map.set(c.id, v);
        }
      }
    }

    if (isAcademy && academyVariants?.length) {
      for (const c of allChildren) {
        const entry = getAcademyModuleEntry(c);
        const vId = entry?.id;
        if (vId != null) {
          const v = academyVariants.find((x: any) => x.id === vId);
          if (v) {
            map.set(c.id, {
              ...v,
              price: v.courseFees ?? 0,
              costAllowed: 0,
            });
          }
        }
      }
    }

    return map;
  }, [isVideoProduction, isAcademy, variants, academyVariants, allChildren]);

  useEffect(() => {
    if (!activeData?.id) return;
    const timer = setTimeout(() => {
      const container = dealsScrollRef.current;
      if (!container) return;
      const selected = container.querySelector(
        "[data-selected='true']"
      ) as HTMLElement | null;
      if (!selected) return;
      const containerRect = container.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();
      const offset =
        selectedRect.top -
        containerRect.top +
        container.scrollTop -
        containerRect.height / 2 +
        selectedRect.height / 2;
      container.scrollTo({
        top: Math.max(0, offset),
        behavior: initialScrollDone.current ? "smooth" : "instant",
      });
      initialScrollDone.current = true;
    }, 50);
    return () => clearTimeout(timer);
  }, [activeData?.id]);

  const status = lead?.status || {};
  const subStatus = lead?.subStatus || {};

  // "Disable deals": the Deals header disappears with the concept.
  const dealsDisabled = useDealsDisabled();
  const hasChildren = !dealsDisabled && allChildren && allChildren.length > 0;
  const { child: activeChildData, isChildActive } = useActiveDataMutation();
  const { canOutboundLead, canOutboundChild, canOutboundPeer } =
    useAssignationPermissions();
  // The `+` next to the assignees opens AssignUserSheet which does double duty:
  // when a child is active it assigns the child, otherwise it assigns the lead.
  // Match the gate to whatever the click will actually do.
  const canAssignFromHere = isChildActive ? canOutboundChild : canOutboundLead;

  // Peer transfer: a role with `outbound_peer` but NOT full outbound can hand a
  // lead/deal it HOLDS to a same-role peer. The same `+` opens the simplified
  // peer sheet (no role picker). `iHoldSlot` / `showAssignPlus` are computed
  // below once the authoritative `users` assignee list is built.
  // Through the accessor: after a reload (every "Switch user" session) the
  // auth slice holds `{ user, token }` and `currentUser.id` is undefined, so
  // a peer-only role never saw its "+" on a reloaded page.
  const currentUserId = useAppSelector(selectCurrentUserId) ?? undefined;
  const peerOnly = canOutboundPeer && !canAssignFromHere;

  // "Request an assignment": ask a person or a role to assign this lead /
  // deal. Offered to everyone but tenant admins (who can assign anyone
  // themselves); the open requests on this slot show as chips beside the +.
  const [requestOpen, setRequestOpen] = useState(false);
  const requestLeadId = lead?.id ? Number(lead.id) : null;
  const requestChildId = isChildActive && activeData?.id ? Number(activeData.id) : null;
  const pendingRequests = usePendingAssignmentRequests(requestLeadId);
  const slotRequests = (pendingRequests.data ?? []).filter((r) =>
    requestChildId ? r.childId === requestChildId : r.childId == null,
  );
  const resolveRequest = useResolveAssignmentRequest();

  const { moveAssignment, unassignAssignee } = useLeadMutations();
  const { updateChild } = useChildMutations();
  const { canGlobal, bypassesRbacChecks, getRawStageModule } = usePermissions();
  const canEditDeal = useCanEditDeal();
  // Renaming a deal is a child-row field edit — gate on lead edit, mirroring
  // the Preference editor's CF-write gate, AND (per chip, below) "Edit
  // choices" on that chip's own deal stage. The server re-checks per-stage.
  const canRenameDeal = bypassesRbacChecks || canGlobal("lead_management", "edit");
  // The user can switch an assignment between lead-level and child-level
  // only when they have both outbound permissions — the destination
  // assignment is a fresh write subject to its level's permission gate.
  const canMoveAssignments = canOutboundLead && canOutboundChild;

  // Unassign — admins, or a role with ADMIN scope on the stage the row lives
  // in (the lead's stage for lead-level rows, the deal's for deal rows). The
  // server re-checks the same rule (`leadService.unassignUser`).
  type WithStage = { stage?: { id?: number } | null; stageId?: number | null } | null | undefined;
  const leadStageId = (lead as WithStage)?.stage?.id ?? (lead as WithStage)?.stageId;
  const activeChildStageId =
    (activeChildData as WithStage)?.stage?.id ?? (activeChildData as WithStage)?.stageId;
  const hasAdminScopeOnStage = (stageId: number | null | undefined) => {
    if (bypassesRbacChecks) return true;
    if (stageId == null || !Number.isFinite(Number(stageId))) return false;
    const lm = getRawStageModule(Number(stageId), "lead_management") as
      | { level?: string }
      | undefined;
    return lm?.level === "admin";
  };

  // "Previous assignees" — org-wide opt-in, admins/super-admins only. When on,
  // former (revoked) assignees are surfaced (dimmed) from the admin-gated
  // revoke-summary endpoint. Gated so the fetch never fires for regular users
  // or when the tenant hasn't enabled it.
  const showPreviousAssignees = useShowPreviousAssignees();
  const canSeePreviousAssignees = bypassesRbacChecks && showPreviousAssignees;
  const { data: revokeSummary } = useLeadRevokeSummary(
    lead?.id ? Number(lead.id) : null,
    { enabled: canSeePreviousAssignees && !!lead?.id },
  );

  // Tracks which row currently has its "Move to" dropdown open. The
  // dropdown renders in a portal outside the popover, so without this the
  // popover's hover-close fires the moment the pointer enters the menu
  // and the move never lands. Combining the dropdown state with
  // `isHovered` keeps the popover anchored while the user navigates the
  // menu, then releases on close.
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const popoverOpen = isHovered || openDropdownKey !== null;

  type AssigneeRow = {
    /** Composite key — `${userId}:${level}:${childId ?? "lead"}` — so the
     * same user assigned at both lead-level and on a child shows as two
     * distinct rows (each with its own badge + move menu), and so two
     * different counsellors on two different deals never collide. */
    key: string;
    id: number;
    name: string;
    roleName: string;
    /** Holder's role id when the payload carries it (Move menu's level rules). */
    roleId?: number | null;
    assignedByAutomation: boolean;
    /** Where this assignment lives. */
    level: "lead" | "child";
    /** Only set when `level === "child"`. */
    childId?: number;
    /** Display label for the deal (only when level === "child"). */
    childLabel?: string;
    /** Server-computed CF-RBAC check: does this assignee actually have
     * access to see this lead/child in their own list? `undefined` means
     * the server didn't annotate (older payload) — treat as ok.
     *
     * For `level === "lead"` this is the any-of-children rule (passes if
     * the user can see the lead via at least one deal). When a deal is
     * active in the drawer, `cfPerChild[activeChildId]` overrides this
     * — a lead-level user who can see the lead overall but not the
     * currently-viewed deal must still get the warning. */
    cfAccessOk?: boolean;
    /** When `cfAccessOk === false`, the specific gated values the user
     * lacks (e.g. `[{ fieldName: "Country", value: "Germany" }]`). Drives
     * the badge tooltip so the viewer can see exactly what's blocking. */
    cfMissing?: { itemId: number; value: string; fieldName: string }[];
    /** Lead-level only: per-deal accessibility map so the warning can
     * flip per active child without a server round-trip. Keys are
     * stringified childIds (from `Object.fromEntries`); look up via
     * `cfPerChild[String(activeChildId)]`. */
    cfPerChild?: Record<
      string,
      { ok: boolean; missing: { itemId: number; value: string; fieldName: string }[] }
    >;
  };

  // "Lead user / Deal user" per role NAME (the rows carry the name only) —
  // the Move menu offers only the levels the holder's role may hold; the
  // server refuses a wrong-level move anyway. Same no-param query the assign
  // drawer's eligibility hook keeps warm on this page.
  const { rolesPipeline: holderRoles } = useRolesPipeline();
  const holderLevelMaps = useMemo(() => {
    const byId = new Map<number, RoleHolderLevels>();
    // Name fallback for payloads without a role id. Names aren't unique: a
    // name shared by roles with DIFFERENT levels falls open (both) — the
    // server's strict move still refuses a wrong-level target.
    const byName = new Map<string, RoleHolderLevels | null>();
    for (const r of (holderRoles ?? []) as any[]) {
      const lv = roleHolderLevels(r);
      if (r?.id != null) byId.set(Number(r.id), lv);
      if (r?.name) {
        const key = String(r.name);
        const prev = byName.get(key);
        byName.set(
          key,
          prev === undefined
            ? lv
            : prev && prev.lead === lv.lead && prev.deal === lv.deal
              ? prev
              : null,
        );
      }
    }
    return { byId, byName };
  }, [holderRoles]);
  const holderLevelsFor = (row: { roleId?: number | null; roleName: string }) =>
    (row.roleId != null ? holderLevelMaps.byId.get(Number(row.roleId)) : undefined) ??
    holderLevelMaps.byName.get(row.roleName) ??
    { lead: true, deal: true };

  // Keyed on `(userId, level, childId)` so a user at both levels surfaces
  // as two rows, and rows on different children don't merge.
  const assigneeMap = new Map<string, AssigneeRow>();
  const keyFor = (userId: number, level: "lead" | "child", childId?: number) =>
    `${userId}:${level}:${childId ?? "lead"}`;

  const dealLabelFor = (child: any): string => {
    if (!child) return "Deal";
    const named = typeof child.name === "string" && child.name.trim();
    if (named) return named;
    const stageName = child?.stage?.name;
    if (stageName) return stageName;
    return `Deal #${child.id}`;
  };

  const pushLeadAssignees = () => {
    const rows = (lead?.leadUsers ?? []) as any[];
    if (rows.length) {
      for (const lu of rows) {
        if (lu?.isRevoked || !lu?.user?.id) continue;
        const key = keyFor(lu.user.id, "lead");
        // When a deal is active, the warning should reflect THIS deal's
        // accessibility, not the lead-level any-of-children result. Per
        // the rule: a France-only counsellor at lead level on a lead
        // with a Germany deal should be flagged when that Germany deal
        // is being viewed, even though they can see the lead overall
        // through another (France) deal.
        const activeChildId = isChildActive ? activeChildData?.id : null;
        const perChild =
          activeChildId != null && lu.cfPerChild
            ? lu.cfPerChild[String(activeChildId)]
            : null;
        const effectiveCfOk = perChild ? perChild.ok : lu.cfAccessOk;
        const effectiveCfMissing = perChild ? perChild.missing : lu.cfMissing;

        assigneeMap.set(key, {
          key,
          id: lu.user.id,
          name: lu.user.name || "Unknown",
          roleName: lu.user?.role?.name ?? "",
          roleId: lu.user?.role?.id ?? lu.user?.roleId ?? null,
          assignedByAutomation: lu.AssignedById == null,
          level: "lead",
          cfAccessOk: effectiveCfOk,
          cfMissing: effectiveCfMissing,
        });
      }
      return;
    }
    for (const u of lead?.users ?? []) {
      if (!u?.id) continue;
      const key = keyFor(u.id, "lead");
      assigneeMap.set(key, {
        key,
        id: u.id,
        name: u.name || "Unknown",
        roleName: (u as any).roleName ?? (u as any).role ?? "",
        assignedByAutomation: Boolean((u as any).assignedByAutomation),
        level: "lead",
      });
    }
  };

  pushLeadAssignees();

  if (isChildActive && activeChildData?.id) {
    const childLabel = dealLabelFor(activeChildData);
    for (const cu of (activeChildData?.childUsers ?? []) as any[]) {
      if (cu?.isRevoked) continue;
      const u = cu?.user;
      if (!u?.id) continue;
      const key = keyFor(u.id, "child", activeChildData.id);
      assigneeMap.set(key, {
        key,
        id: u.id,
        name: u.name || "Unknown",
        roleName: u.role?.name || "",
        roleId: u.role?.id ?? u.roleId ?? null,
        assignedByAutomation: false,
        level: "child",
        childId: activeChildData.id,
        childLabel,
        cfAccessOk: cu.cfAccessOk,
        cfMissing: cu.cfMissing,
      });
    }
  }

  const users = Array.from(assigneeMap.values());

  // Former (revoked) assignees to show dimmed under the active ones. A user who
  // is still active anywhere on this lead (re-assigned since) is NOT "previous",
  // so drop any userId present in the active list. One row per (user, level,
  // child); most-recently-revoked first.
  const activeUserIds = new Set(users.map((u) => Number(u.id)));
  const previousUsers: PreviousUser[] = (() => {
    if (!canSeePreviousAssignees) return [];
    const seen = new Set<string>();
    const out: PreviousUser[] = [];
    for (const e of revokeSummary?.entries ?? []) {
      if (activeUserIds.has(Number(e.userId))) continue;
      const key = `${e.userId}:${e.level}:${e.childId ?? "lead"}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        userId: Number(e.userId),
        name: e.userName || "Unknown",
        roleName: e.roleName ?? "",
        level: e.level,
        childName: e.childName,
        revokedAt: e.revokedAt,
        revokedByName: e.revokedByName,
        cause: e.cause,
      });
    }
    out.sort((a, b) => {
      const ta = a.revokedAt ? new Date(a.revokedAt).getTime() : 0;
      const tb = b.revokedAt ? new Date(b.revokedAt).getTime() : 0;
      return tb - ta;
    });
    return out;
  })();

  /** "revoked 2 days ago by Asha" — best-effort, tolerant of a null/bad date. */
  const revokedMeta = (pu: PreviousUser): string => {
    const d = pu.revokedAt ? new Date(pu.revokedAt) : null;
    // Guard against an unparseable timestamp: date-fns throws on Invalid Date,
    // which would crash the whole drawer render.
    const when =
      d && !Number.isNaN(d.getTime())
        ? formatDistanceToNow(d, { addSuffix: true })
        : null;
    const by = pu.revokedByName ? `by ${pu.revokedByName}` : null;
    return ["Revoked", when, by].filter(Boolean).join(" ");
  };

  // Peer-transfer "+" gate: show it for full-assign roles, OR for peer-only
  // roles that actually HOLD this slot (lead-level vs the active deal). Derived
  // from the authoritative assignee list so it tolerates every lead-shape
  // (leadUsers / users / childUsers).
  const iHoldSlot = users.some(
    (u) =>
      Number(u.id) === Number(currentUserId) &&
      (isChildActive ? u.level === "child" : u.level === "lead"),
  );
  const showAssignPlus = canAssignFromHere || (peerOnly && iHoldSlot);

  const childrenGroupedByStage = useMemo(() => {
    if (!allChildren?.length) return [];

    const stageMap = new Map<number, { stage: any; children: any[] }>();

    for (const child of allChildren) {
      const stageId = child.stageId || child.stage?.id;
      if (!stageMap.has(stageId)) {
        const pipelineStage = pipeline?.find((s: any) => s.id === stageId);
        stageMap.set(stageId, {
          stage: pipelineStage ||
            child.stage || { id: stageId, name: "Unknown", order: 999 },
          children: [],
        });
      }
      stageMap.get(stageId)!.children.push(child);
    }

    return Array.from(stageMap.values()).sort(
      (a, b) => (a.stage.order ?? 0) - (b.stage.order ?? 0)
    );
  }, [allChildren, pipeline]);

  // When the "Verified" filter is active on the leads list, the lead view shows
  // only the ONE verified deal — the representative pick: among the deals that
  // REACHED the Project Starting Stage, the ACTIVE (non-rejected) one, earliest
  // created on a tie. There is no "finished" concept — the last stage (Enrolled)
  // is just a stage, so a deal is never ranked down for being further along.
  // The ACTIVE anchor's stage id (synced from the list's selected anchor), or
  // null when no anchor filter is active.
  const activeAnchorId = useAppSelector(
    (s: any) => s.leadFunctional?.verifiedDealView
  ) as number | null;
  const isVerifiedView = activeAnchorId != null;

  // ACTIVE INTAKE FILTER — the exact (year, month) combinations the user ticked
  // (`cfPair_<yearField>_<monthField>` on the wire). Year and month are ANDed
  // and must be ALIGNED ON ONE DEAL: a deal is "in the selected intake" only if
  // it carries BOTH halves of the SAME combination. A deal holding 2026 from one
  // pair and January from another is NOT a match.
  // Read the ACTIVE surface's params — /leads and dynamic routes (stage tabs,
  // Favorite Views) keep their filters in different slices, and this component
  // renders on all of them. Two earlier attempts read a slice directly and were
  // inert: first the wrong key (`leadFilter` vs `leadFilterSlice`), then the
  // right key but the wrong SLICE for Favorite Views. Parsing the serialized
  // params is surface-independent and is exactly what the server received.
  const activeFilterParams = useActiveLeadFilterParams();
  const selectedIntakePairs = useMemo<Array<[string, string]>>(
    () => parseCfPairsFromParams(activeFilterParams),
    [activeFilterParams]
  );

  /** Does THIS deal carry both halves of one selected combination? */
  const dealMatchesIntake = useCallback(
    (c: any) => {
      if (selectedIntakePairs.length === 0) return true; // no intake filter
      const ids = new Set<string>();
      for (const sc of c?.customInputScopes ?? []) {
        for (const id of sc?.optionIds ?? []) ids.add(String(id));
      }
      return selectedIntakePairs.some(([a, b]) => ids.has(a) && ids.has(b));
    },
    [selectedIntakePairs]
  );

  const verifiedChildId = useMemo<number | null>(() => {
    if (!allChildren?.length || !pipeline?.length || activeAnchorId == null)
      return null;

    // THE SERVER'S ANSWER WINS. `matchedChildId` is the representative deal the
    // leads list and the anchor COUNT actually used — computed with the active
    // Intake/Country filter in hand. The local pick below only knows stage
    // order, so on a lead holding deals in two intakes it happily chose the one
    // the filter EXCLUDED: tenant 26, lead 276078, filtered Nov-26…Mar-27, the
    // count matched its Jan-2027 Offer Letter deal, the drawer opened its
    // Sep-2026 Pre Visa deal, and the intake filter looked broken.
    // Falls through to the local pick when the server didn't send one (no
    // anchor forwarded, or an older API), so nothing regresses.
    const serverPick = (lead as any)?.matchedChildId;
    if (serverPick != null && allChildren.some((c: any) => Number(c?.id) === Number(serverPick))) {
      return Number(serverPick);
    }

    // Pick the verified deal relative to the ACTIVE anchor stage.
    const projectStage = pipeline.find((s: any) => s?.id === activeAnchorId);
    if (!projectStage) return null;

    const orderById = new Map<number, number>(
      pipeline.map((s: any) => [s.id, s.order ?? 0])
    );
    const projectOrder = projectStage.order ?? 0;
    const orderOf = (c: any) =>
      orderById.get(c.stageId || c.stage?.id) ?? c.stage?.order ?? 0;

    // Deals that reached the project stage (at it or any later stage) AND —
    // when an intake filter is on — that actually SIT in one of the selected
    // intakes. Without this second half the pick was made on stage order alone,
    // so a lead holding a Jan-2027 Offer Letter deal and a Sep-2026 Pre Visa
    // deal opened on the September one: an intake the user never selected.
    const reachedAll = allChildren.filter((c: any) => orderOf(c) >= projectOrder);
    const reachedInIntake = reachedAll.filter(dealMatchesIntake);
    // Fall back to the unfiltered set only if NO reached deal matches the
    // intake — better to show something than an empty drawer.
    const reached = reachedInIntake.length ? reachedInIntake : reachedAll;
    if (!reached.length) return null;

    const preferActive = projectStage.verifyPreferActive !== false;
    const tieBreakEarliest = projectStage.verifyTieBreakEarliest !== false;
    const preferLatestStage = projectStage.verifyPreferLatestStage === true;

    // Resolve `isRejection` per status from the pipeline (authoritative). The
    // deal objects here (from `useChilds`) do NOT reliably carry
    // `status.isRejection`, so reading it off the child alone misses every
    // rejection — and the picker then lands on a furthest-along REJECTED deal
    // (the bug: a verified lead's drawer opening on its rejected higher-stage
    // deal). Mirrors StageSection's resolution.
    const rejectionByStatusId = new Map<number, boolean>();
    const rejectionBySubStatusId = new Map<number, boolean>();
    for (const stg of pipeline as any[]) {
      const statuses = stg?.statuses || stg?.statusList || [];
      for (const st of statuses) {
        rejectionByStatusId.set(Number(st.id), !!st.isRejection);
        for (const ss of st?.subStatuses || []) {
          rejectionBySubStatusId.set(Number(ss.id), !!ss.isRejection);
        }
      }
    }
    const isRejected = (c: any) => {
      // Two-level rejection: Status.isRejection OR SubStatus.isRejection
      // (mirrors the server's rejection.where.ts).
      const subStatusId = c?.subStatusId ?? c?.subStatus?.id;
      if (
        (typeof c?.subStatus?.isRejection === "boolean" &&
          c.subStatus.isRejection) ||
        (subStatusId != null &&
          rejectionBySubStatusId.get(Number(subStatusId)) === true)
      ) {
        return true;
      }
      if (typeof c?.status?.isRejection === "boolean")
        return c.status.isRejection;
      const statusId = c.statusId ?? c.status?.id;
      return rejectionByStatusId.get(Number(statusId)) === true;
    };

    // The verified deal is the ACTIVE one — i.e. not in a rejection status.
    const candidates = preferActive
      ? reached.filter((c: any) => !isRejected(c))
      : reached;
    if (!candidates.length) return null;

    const sorted = [...candidates].sort((a: any, b: any) => {
      // Furthest-along first (the deal in the latest stage), when enabled.
      if (preferLatestStage) {
        const diff = orderOf(b) - orderOf(a);
        if (diff !== 0) return diff;
      }
      // Fallback / default: the one that reached the project stage first —
      // its `projectReachedAt` (application-created date), createdAt if unstamped.
      if (tieBreakEarliest) {
        const at = new Date(a.projectReachedAt ?? a.createdAt ?? 0).getTime();
        const bt = new Date(b.projectReachedAt ?? b.createdAt ?? 0).getTime();
        return at - bt;
      }
      return 0;
    });
    return sorted[0]?.id ?? null;
  }, [allChildren, pipeline, activeAnchorId, lead, dealMatchesIntake]);

  // EVERY deal the viewer can read stays listed — under the Verified view too
  // (owner, 2026-10-22: "show all the deals, if they have access to"). The
  // view used to keep only the verified deal: with "Verified Offer Letter" on
  // and the list filtered "Stage: Visa · Status: Visa Rejected", the drawer
  // showed one Offer Letter deal and hid the visa-rejected one the filter
  // had matched, so five listed students looked like they were not in Visa
  // Rejected at all. The verified deal is still the one opened by default.
  const displayedGroups = childrenGroupedByStage;

  // In verified view, open on the verified deal so the detail panel (Course,
  // Preference, etc.) shows ITS data — not the lead's default furthest child.
  // ONCE per (lead, verified deal): the deals are all listed now, so a manual
  // switch afterwards must stand. The list's own Stage / Status chips pick
  // first (ChildProvider routeStageId → the deal carrying the chips): when a
  // deal in that stage exists the verified pick stands down, so the drawer
  // opens on what the viewer filtered for.
  const verifiedPickRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isVerifiedView || !verifiedChildId) {
      verifiedPickRef.current = null;
      return;
    }
    const key = `${lead?.id ?? ""}:${verifiedChildId}`;
    if (verifiedPickRef.current === key) return;
    const chipsPickTheDeal =
      routeStageId != null &&
      (allChildren ?? []).some(
        (c: any) => Number(c?.stageId ?? c?.stage?.id) === Number(routeStageId),
      );
    verifiedPickRef.current = key;
    if (chipsPickTheDeal) return;
    if (activeChildData?.id !== verifiedChildId) handleChildChange(verifiedChildId);
  }, [
    isVerifiedView,
    verifiedChildId,
    routeStageId,
    allChildren,
    activeChildData?.id,
    handleChildChange,
    lead?.id,
  ]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4 items-start justify-between">
        {!hasChildren && (
          <>
            <div className="flex-1 flex flex-col gap-0.5 items-start justify-center min-w-0 min-h-0">
              <p className="text-[12px] font-normal text-[#697571] tracking-[-0.24px] leading-[1.35]">
                Status
              </p>
              <p
                className="text-[14px] font-medium tracking-[-0.28px] leading-normal"
                style={{ color: SUB_STATUS_COLORS[subStatus] || "#c800d7" }}
              >
                {status?.name || "In Progress"}
              </p>
            </div>

            <div className="flex-1 flex flex-col gap-0.5 items-start justify-center min-w-0 min-h-0">
              <p className="text-[12px] font-normal text-[#697571] tracking-[-0.24px] leading-[1.35]">
                Sub-status
              </p>
              <p
                className="text-[14px] font-medium tracking-[-0.28px] leading-normal"
                style={{ color: SUB_STATUS_COLORS[subStatus] || "#ff8d13" }}
              >
                {subStatus?.name || "In Progress"}
              </p>
            </div>
          </>
        )}

        {hasChildren && (
          <div className="flex-1 flex flex-col gap-0 min-w-0 overflow-hidden">
            <p className="text-[11px] font-semibold text-[#1e302a] tracking-[-0.11px] leading-[1.35] mb-1.5">
              Deals
            </p>
            <div
              ref={dealsScrollRef}
              // ALL the deals in view (owner, 2026-10-22: "show all the deals"):
              // the 100px slot showed one stage group and hid the rest behind
              // an invisible scrollbar — the Visa-rejected deal under two Offer
              // Letter deals never came into sight. Tall enough for four stage
              // groups; longer histories still scroll.
              className="flex flex-col gap-0 relative max-h-[240px] overflow-y-auto pr-1"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#d6f0e3 transparent",
              }}
            >
              {displayedGroups.map((group, groupIdx) => {
                const isLast = groupIdx === displayedGroups.length - 1;
                return (
                  <div
                    key={group.stage.id}
                    className="flex items-stretch gap-0 relative"
                  >
                    {/* Timeline rail */}
                    <div className="flex flex-col items-center shrink-0 w-[18px] relative">
                      <div className="w-[7px] h-[7px] rounded-full bg-[#46a580] border-[2px] border-[#d6f0e3] shrink-0 mt-[9px] z-[1]" />
                      {!isLast && (
                        <div className="w-[1px] flex-1 bg-[#d6f0e3]" />
                      )}
                    </div>

                    {/* Stage content */}
                    <div
                      className={`flex flex-col gap-1 flex-1 min-w-0 ${isLast ? "pb-0" : "pb-3"}`}
                    >
                      <span className="text-[9.5px] font-semibold text-[#8a9590] uppercase tracking-[0.5px] whitespace-nowrap leading-[22px]">
                        {group.stage.name}
                      </span>
                      <div className="flex gap-1 items-center flex-wrap">
                        {group.children.map((c: any) => (
                          <DealChip
                            key={c.id}
                            child={c}
                            isSelected={activeData?.id === c.id}
                            onClick={() => handleChildChange(c.id)}
                            variant={variantLookup.get(c.id)}
                            canRename={
                              canRenameDeal &&
                              canEditDeal(c.stageId ?? group.stage.id)
                            }
                            onRename={(name) =>
                              updateChild.mutate(
                                { id: c.id, data: { name } },
                                {
                                  onSuccess: () =>
                                    toast.success("Deal renamed"),
                                }
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-0.5 items-start justify-center min-w-0 min-h-0 shrink-0">
          <p className="text-[12px] font-normal text-[#697571] tracking-[-0.24px] leading-[1.35]">
            Assigned to
          </p>
          <div className="flex gap-2 items-start">
            <Popover open={popoverOpen} onOpenChange={setIsHovered}>
              <PopoverTrigger asChild>
                <div
                  className="flex items-center pr-2 cursor-pointer"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {users?.slice(0, 3).map((user, index: number) => {
                    const colors = AVATAR_COLORS[index % AVATAR_COLORS.length];
                    return (
                      <Avatar
                        key={`${user.id}-${index}`}
                        className="size-8 -mr-2 cursor-default border-2 border-white"
                        style={{ backgroundColor: colors.bg }}
                      >
                        <AvatarFallback
                          className="text-[14px] font-medium tracking-[-0.084px] leading-[20px]"
                          style={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                          }}
                        >
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                    );
                  })}
                  {previousUsers.length > 0 &&
                    (() => {
                      // Keep the inline stack tidy: active takes up to 3 slots,
                      // previous fill the rest up to 5; overflow collapses to +N.
                      const cap = Math.max(0, 5 - Math.min(users.length, 3));
                      const inlinePrev = previousUsers.slice(0, cap);
                      const extra = previousUsers.length - inlinePrev.length;
                      return (
                        <>
                          {inlinePrev.map((pu) => (
                            <Avatar
                              key={pu.key}
                              title={`Previously assigned${
                                pu.roleName ? ` · ${pu.roleName}` : ""
                              } · ${revokedMeta(pu)}`}
                              className="size-8 -mr-2 cursor-default border-2 border-dashed border-[#c5cbc8] opacity-40 grayscale"
                              style={{ backgroundColor: "#e1e4ea" }}
                            >
                              <AvatarFallback
                                className="text-[14px] font-medium tracking-[-0.084px] leading-[20px]"
                                style={{
                                  backgroundColor: "#e1e4ea",
                                  color: "#0e121b",
                                }}
                              >
                                {getInitials(pu.name)}
                              </AvatarFallback>
                            </Avatar>
                          ))}
                          {extra > 0 && (
                            <div
                              title={`${extra} more previous assignee${
                                extra > 1 ? "s" : ""
                              }`}
                              className="size-8 -mr-2 flex items-center justify-center rounded-full border-2 border-dashed border-[#c5cbc8] bg-[#eef0ef] text-[10px] font-semibold text-[#697571]"
                            >
                              +{extra}
                            </div>
                          )}
                        </>
                      );
                    })()}
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="w-[min(100vw-24px,360px)] p-0 rounded-[12px] border border-[#e6e8e7] shadow-lg"
                align="start"
                sideOffset={8}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div className="flex flex-col gap-2 px-3 py-2.5">
                  <p className="text-[13px] font-semibold leading-tight tracking-[-0.2px] text-[#051912]">
                    Assigned to
                  </p>
                  <div className="flex flex-col gap-0.5">
                    {users && users.length > 0 ? (
                      users.map((user, index: number) => {
                        const colors =
                          AVATAR_COLORS[index % AVATAR_COLORS.length];
                        // Move targets exclude the row's own slot. For a
                        // lead-level row, every deal is a valid destination.
                        // For a child-level row, "lead" is one option and
                        // sibling deals are the rest. The mutation key on
                        // the menu items keeps it disabled while in flight
                        // so a double-click can't fire two moves.
                        const holderLevels = holderLevelsFor(user);
                        const moveTargets = (
                          user.level === "lead"
                            ? (allChildren ?? []).map((c: any) => ({
                                kind: "child" as const,
                                childId: c.id,
                                label: dealLabelFor(c),
                              }))
                            : [
                                {
                                  kind: "lead" as const,
                                  label: "Lead level",
                                },
                                ...(allChildren ?? [])
                                  .filter((c: any) => c.id !== user.childId)
                                  .map((c: any) => ({
                                    kind: "child" as const,
                                    childId: c.id,
                                    label: dealLabelFor(c),
                                  })),
                              ]
                        ).filter((t) =>
                          t.kind === "lead" ? holderLevels.lead : holderLevels.deal,
                        );
                        const canMoveHere =
                          canMoveAssignments && moveTargets.length > 0;
                        const canUnassignRow = hasAdminScopeOnStage(
                          user.level === "child" ? activeChildStageId : leadStageId,
                        );
                        return (
                          <div
                            key={user.key}
                            className="group flex min-w-0 items-center gap-2 rounded-lg py-1.5 pr-1 transition-colors hover:bg-[#f4f6f5]"
                          >
                            <Avatar
                              className="size-7 shrink-0"
                              style={{ backgroundColor: colors.bg }}
                            >
                              <AvatarFallback
                                className="text-[11px] font-medium leading-none tracking-tight"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                }}
                              >
                                {getInitials(user.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 items-start gap-1">
                                <p className="break-words text-[12px] font-medium leading-tight tracking-[-0.2px] text-[#051912]">
                                  {user.name}
                                </p>
                                {user.assignedByAutomation ? (
                                  <Star
                                    className="mt-[2px] size-3 shrink-0 fill-violet-600 text-violet-600"
                                    strokeWidth={1.75}
                                    aria-label="Assigned by automation"
                                  />
                                ) : null}
                                {user.cfAccessOk === false ? (
                                  <Tooltip delayDuration={150}>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="mt-[2px] inline-flex shrink-0 cursor-help"
                                        aria-label="CF-restricted view"
                                      >
                                        <AlertTriangle
                                          className="size-3 fill-amber-100 text-amber-600"
                                          strokeWidth={2}
                                        />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      align="center"
                                      className="max-w-[280px] text-[11px] leading-snug"
                                    >
                                      <p className="font-semibold">
                                        Restricted view
                                      </p>
                                      {user.cfMissing && user.cfMissing.length > 0 ? (
                                        <>
                                          <p className="mt-1 opacity-90">
                                            Missing Custom Field access:
                                          </p>
                                          <ul className="mt-0.5 ml-3 list-disc">
                                            {user.cfMissing.map((m) => (
                                              <li key={m.itemId}>
                                                <span className="font-medium">
                                                  {m.fieldName}
                                                </span>
                                                : {m.value}
                                              </li>
                                            ))}
                                          </ul>
                                          <p className="mt-1 opacity-80">
                                            They're assigned, but won't see{" "}
                                            {user.level === "lead"
                                              ? "this lead"
                                              : "this deal"}{" "}
                                            in their own list until granted.
                                          </p>
                                        </>
                                      ) : (
                                        <p className="mt-1 opacity-90">
                                          This user is assigned but Custom Field
                                          access prevents them from seeing{" "}
                                          {user.level === "lead"
                                            ? "this lead"
                                            : "this deal"}{" "}
                                          in their own list.
                                        </p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null}
                              </div>
                              <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                                <p className="truncate text-[11px] leading-snug text-[#697571]">
                                  {user.roleName?.trim() || "No role on file"}
                                </p>
                                <span className="text-[9px] leading-none text-[#c5cbc8]">
                                  •
                                </span>
                                <span
                                  className={`shrink-0 max-w-[160px] truncate rounded-full px-1.5 py-[1px] text-[9.5px] font-semibold uppercase tracking-[0.4px] ${
                                    user.level === "lead"
                                      ? "bg-[#e6f3ed] text-[#156548]"
                                      : "bg-[#eef1ff] text-[#3a4ba8]"
                                  }`}
                                  title={
                                    user.level === "lead"
                                      ? "Assigned at lead level — visible on every deal under this lead"
                                      : `Assigned to deal: ${user.childLabel ?? `#${user.childId}`}`
                                  }
                                >
                                  {user.level === "lead"
                                    ? "Lead"
                                    : (user.childLabel ?? "Deal")}
                                </span>
                              </div>
                            </div>
                            {lead?.id && (canMoveHere || canUnassignRow) && (
                              <DropdownMenu
                                open={openDropdownKey === user.key}
                                onOpenChange={(open) =>
                                  setOpenDropdownKey(open ? user.key : null)
                                }
                              >
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Assignee actions"
                                    className="shrink-0 self-start mt-0.5 rounded-md p-1 text-[#697571] opacity-0 transition hover:bg-[#e6e8e7] hover:text-[#051912] group-hover:opacity-100 data-[state=open]:opacity-100"
                                    disabled={
                                      moveAssignment.isPending ||
                                      unassignAssignee.isPending
                                    }
                                  >
                                    <MoreHorizontal className="size-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="min-w-[200px]"
                                >
                                  {canMoveHere && (
                                    <>
                                      <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#697571]">
                                        Move to
                                      </DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      {moveTargets.map((t) => (
                                        <DropdownMenuItem
                                          key={
                                            t.kind === "lead"
                                              ? "lead"
                                              : `child-${t.childId}`
                                          }
                                          disabled={moveAssignment.isPending}
                                          onSelect={() => {
                                            moveAssignment.mutate({
                                              leadId: Number(lead.id),
                                              userId: user.id,
                                              fromChildId:
                                                user.level === "child"
                                                  ? (user.childId ?? null)
                                                  : null,
                                              toChildId:
                                                t.kind === "child"
                                                  ? t.childId
                                                  : null,
                                            });
                                          }}
                                        >
                                          {t.label}
                                        </DropdownMenuItem>
                                      ))}
                                    </>
                                  )}
                                  {canUnassignRow && (
                                    <>
                                      {canMoveHere && <DropdownMenuSeparator />}
                                      <DropdownMenuItem
                                        disabled={unassignAssignee.isPending}
                                        className="text-[var(--color-error)] focus:text-[var(--color-error)]"
                                        onSelect={() => {
                                          unassignAssignee.mutate({
                                            leadId: Number(lead.id),
                                            userId: user.id,
                                            childId:
                                              user.level === "child"
                                                ? (user.childId ?? null)
                                                : null,
                                            userName: user.name,
                                          });
                                        }}
                                      >
                                        Unassign
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="py-1 text-[12px] leading-snug text-[#697571]">
                        No users assigned
                      </p>
                    )}
                  </div>

                  {previousUsers.length > 0 && (
                    <div className="flex flex-col gap-0.5 border-t border-[#f0f2f1] pt-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#697571]">
                        Previously assigned
                      </p>
                      {previousUsers.map((pu) => (
                        <div
                          key={pu.key}
                          className="flex min-w-0 items-center gap-2 rounded-lg py-1.5 pr-1 opacity-60"
                        >
                          <Avatar
                            className="size-7 shrink-0 grayscale"
                            style={{ backgroundColor: "#e1e4ea" }}
                          >
                            <AvatarFallback
                              className="text-[11px] font-medium leading-none tracking-tight"
                              style={{
                                backgroundColor: "#e1e4ea",
                                color: "#0e121b",
                              }}
                            >
                              {getInitials(pu.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-center gap-1.5">
                              <p className="break-words text-[12px] font-medium leading-tight tracking-[-0.2px] text-[#051912]">
                                {pu.name}
                              </p>
                              <span className="shrink-0 rounded-full bg-[#eef0ef] px-1.5 py-[1px] text-[9.5px] font-semibold uppercase tracking-[0.4px] text-[#697571]">
                                Previous
                              </span>
                            </div>
                            <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                              <p className="truncate text-[11px] leading-snug text-[#697571]">
                                {pu.roleName?.trim() || "No role on file"}
                              </p>
                              <span className="text-[9px] leading-none text-[#c5cbc8]">
                                •
                              </span>
                              <span
                                className="shrink-0 max-w-[140px] truncate rounded-full bg-[#eef1f0] px-1.5 py-[1px] text-[9.5px] font-semibold uppercase tracking-[0.4px] text-[#697571]"
                                title={
                                  pu.level === "lead"
                                    ? "Was assigned at lead level"
                                    : `Was assigned to deal: ${pu.childName ?? ""}`
                                }
                              >
                                {pu.level === "lead"
                                  ? "Lead"
                                  : (pu.childName ?? "Deal")}
                              </span>
                            </div>
                            <p className="mt-0.5 truncate text-[10.5px] leading-snug text-[#9199a6]">
                              {revokedMeta(pu)}
                              {pu.cause
                                ? ` · ${REVOKE_CAUSE_LABEL[pu.cause]}`
                                : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {showAssignPlus && (
              <Button
                onClick={() => {
                  // Full-assign roles get the normal drawer; peer-only roles
                  // get the simplified same-role peer handoff sheet.
                  if (canAssignFromHere) {
                    dispatch(setIsAssign(true));
                  } else {
                    dispatch(setIsPeerTransfer(true));
                  }
                }}
                variant="outline"
                size="icon"
                className="size-8 rounded-full border-[1.5px] border-dashed border-[#9ba3a0] bg-[#f7f7f7] hover:bg-[#efefef] p-[6px]"
              >
                <Plus className="size-[18px] text-[#374741]" strokeWidth={2} />
              </Button>
            )}
            {!bypassesRbacChecks && requestLeadId != null && (
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setRequestOpen(true)}
                    variant="outline"
                    size="icon"
                    aria-label="Request an assignment"
                    className="size-8 rounded-full border-[1.5px] border-dashed border-amber-300 bg-amber-50/60 hover:bg-amber-50 p-[6px]"
                  >
                    <UserRoundPlus className="size-[16px] text-amber-700" strokeWidth={2} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-[11px]">
                  Ask someone to assign this {isChildActive ? "deal" : "lead"}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {slotRequests.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {slotRequests.map((r) => (
                <AssignmentRequestChip
                  key={r.id}
                  request={r}
                  mine={Number(r.requestedBy.id) === Number(currentUserId)}
                  busy={resolveRequest.isPending}
                  onResolve={(action) =>
                    resolveRequest.mutate(
                      { id: r.id, action, alertId: r.alertId },
                      {
                        onSuccess: () =>
                          toast.success(
                            action === "assign"
                              ? `Assigned to ${r.suggested?.name ?? "them"}`
                              : action === "done"
                                ? "Marked as done"
                                : "Request cancelled",
                          ),
                        onError: (err) =>
                          toast.error(
                            assignmentRequestErrorMessage(
                              err,
                              action === "assign" ? "Could not assign" : "Could not update the request",
                            ),
                          ),
                      },
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <AssignRequestDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        leadId={requestLeadId}
        childId={requestChildId}
      />
    </div>
  );
}

/** "Asked Branch Manager · 2h ago" — the requester can cancel; a recipient can assign to the suggested person, or mark it done. */
function AssignmentRequestChip({
  request,
  mine,
  busy,
  onResolve,
}: {
  request: PendingAssignmentRequest;
  mine: boolean;
  busy: boolean;
  onResolve: (action: "done" | "cancel" | "assign") => void;
}) {
  const who = mine ? "You asked" : `${request.requestedBy.name?.trim() || "Someone"} asked`;
  return (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-full border border-amber-200 bg-amber-50 pl-2 pr-1 py-0.5 text-[10.5px] text-amber-800"
      title={
        request.suggested?.name
          ? `Suggested: ${request.suggested.name}${request.note ? ` · ${request.note}` : ""}`
          : request.note ?? undefined
      }
    >
      <span className="truncate">
        {who} <span className="font-semibold">{request.target.name}</span>
        {request.suggested?.name ? ` → ${request.suggested.name}` : ""} · {alertTimeAgo(request.createdAt)}
      </span>
      {request.forMe && !mine && request.suggested?.name && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve("assign")}
          title={`Assign to ${request.suggested.name}`}
          className="rounded-full bg-amber-700 px-1.5 py-px font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
        >
          Assign
        </button>
      )}
      {request.forMe && !mine && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve("done")}
          title="Close the request without assigning"
          className="rounded-full bg-white/80 px-1.5 py-px font-semibold text-amber-800 hover:bg-white disabled:opacity-50"
        >
          Done
        </button>
      )}
      {mine && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve("cancel")}
          aria-label="Cancel the request"
          title="Cancel the request"
          className="flex size-4 items-center justify-center rounded-full text-amber-700 hover:bg-white/80 disabled:opacity-50"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}
